// pages/ViewGreetingCard.js - View Greeting Card with Envelope Animation
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShare2, FiEdit2, FiX, FiMail } from 'react-icons/fi';
import { getGreetingCardBySlug } from '../services/greetingCardService';
import { getFontById } from '../constants/cardFonts';
import { getEnvelopeStyleById } from '../constants/envelopeStyles';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import '../styles/ViewGreetingCard.scss';

import { cardUrl } from '../shared/shortLinks';
const ViewGreetingCard = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const audioRef = useRef(null);

  const [card, setCard] = useState(null);
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [soundPlayed, setSoundPlayed] = useState(false);

  useEffect(() => {
    loadCard();
  }, [slug]);

  const loadCard = async () => {
    try {
      setLoading(true);
      const result = await getGreetingCardBySlug(slug);

      if (!result || result.accessDenied) {
        alert('You do not have permission to view this card.');
        navigate('/explore');
        return;
      }

      if (!result.card) {
        alert('Card not found');
        navigate('/explore');
        return;
      }

      setCard(result.card);

      // Fetch creator profile
      const { data: creatorData } = await supabase
        .from('users')
        .select('id, first_name, last_name, profile_photo_cdn_url, avatar_config')
        .eq('id', result.card.creator_id)
        .single();

      if (creatorData) {
        setCreator(creatorData);
      }

      // Check if current user is creator
      if (user && result.card.creator_id === user.id) {
        setIsCreator(true);
      }
    } catch (error) {
      console.error('Error loading card:', error);
      alert('Failed to load card');
      navigate('/explore');
    } finally {
      setLoading(false);
    }
  };

  const playEnvelopeAnimation = () => {
    if (isOpen) return;

    // Play sound once
    if (!soundPlayed && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => console.log('Audio play error:', err));
      setSoundPlayed(true);
    }

    setIsOpen(true);
  };

  const getCoverImageUrl = () => {
    if (!card) return null;

    if (card.cover_image_type === 'gif') {
      return card.cover_static_gif_cdn_url || card.cover_image_cdn_url;
    }
    if (card.cover_image_type === 'youtube') {
      return card.youtube_thumbnail_cdn_url || card.cover_thumbnail_cdn_url;
    }
    if (card.cover_image_type === 'pdf' && card.pdf_page_images?.length > 0) {
      return card.pdf_page_images[0].cdn_url;
    }
    return card.cover_image_cdn_url || card.cover_thumbnail_cdn_url;
  };

  const getSenderName = () => {
    if (!creator) return 'Someone';
    const firstName = creator.first_name || '';
    const lastName = creator.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'Someone';
  };

  const getShareableUrl = () => {
    return cardUrl(slug, window.location.origin);
  };

  const handleShare = () => {
    const url = getShareableUrl();

    if (navigator.share) {
      navigator.share({
        title: card.title,
        text: `Check out this greeting card: ${card.title}`,
        url: url
      }).catch(err => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  const getFontStyle = () => {
    if (!card?.font_family) return {};
    const font = getFontById(card.font_family);
    if (!font || font.id === 'default') return {};
    return { fontFamily: font.fontFamily };
  };

  const getEnvelopeStyle = () => {
    if (!card?.envelope_style) {
      return getEnvelopeStyleById('classic');
    }
    return getEnvelopeStyleById(card.envelope_style);
  };

  if (loading) {
    return (
      <div className="view-greeting-card-page loading">
        <div className="spinner"></div>
        <p>Loading card...</p>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="view-greeting-card-page error">
        <FiMail className="error-icon" />
        <h2>Card Not Found</h2>
        <button className="btn btn-primary" onClick={() => navigate('/explore')}>
          Go to Explore
        </button>
      </div>
    );
  }

  const envelopeStyle = getEnvelopeStyle();

  return (
    <div
      className="view-greeting-card-page"
      style={{
        backgroundColor: card.theme_color || '#7C4DFF',
      }}
    >
      {/* Background overlay */}
      <div className="background-overlay" />

      {/* Audio */}
      <audio ref={audioRef} src="/sounds/paper-open.mp3" />

      {/* Edit Button */}
      {isCreator && (
        <button
          className="btn-edit"
          onClick={() => navigate(`/edit-greeting-card/${card.id}`)}
          title="Edit Card"
        >
          <FiEdit2 />
        </button>
      )}

      {/* Sender Section */}
      <div className="sender-section">
        <p className="from-label">From</p>
        <div className="sender-info">
          {creator?.profile_photo_cdn_url ? (
            <img
              src={creator.profile_photo_cdn_url}
              alt={getSenderName()}
              className="sender-avatar"
            />
          ) : (
            <div className="sender-avatar-placeholder">
              {getSenderName().charAt(0).toUpperCase()}
            </div>
          )}
          <h3 className="sender-name">{getSenderName()}</h3>
        </div>
      </div>

      {/* Animation Container */}
      <div
        className="animation-container"
        onClick={playEnvelopeAnimation}
        role={isOpen ? undefined : 'button'}
        tabIndex={isOpen ? undefined : 0}
        aria-label={isOpen ? undefined : 'Open card'}
        onKeyDown={(e) => {
          if (!isOpen && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            playEnvelopeAnimation();
          }
        }}
        style={isOpen ? undefined : { cursor: 'pointer' }}
      >
        {/* Envelope */}
        <motion.div
          className={`envelope-wrapper ${isOpen ? 'opened' : ''}`}
          animate={isOpen ? {
            y: '100vh',
            opacity: 0,
            transition: { duration: 0.8, delay: 1.5 }
          } : {}}
        >
          <div
            className="envelope"
            style={{
              background: `linear-gradient(135deg, ${envelopeStyle.bodyColors.join(', ')})`,
              borderColor: envelopeStyle.borderColor,
            }}
          >
            {/* Ribbon */}
            <motion.div
              className="ribbon-vertical"
              style={{ backgroundColor: envelopeStyle.ribbonColor }}
              animate={isOpen ? { opacity: 0, transition: { duration: 0.3, delay: 0.3 } } : {}}
            />
            <motion.div
              className="ribbon-horizontal"
              style={{ backgroundColor: envelopeStyle.ribbonColor }}
              animate={isOpen ? { opacity: 0, transition: { duration: 0.3, delay: 0.3 } } : {}}
            />

            {/* Bow */}
            <motion.div
              className="bow-center"
              animate={isOpen ? { opacity: 0, scale: 0.8, transition: { duration: 0.3, delay: 0.3 } } : {}}
            >
              <div
                className="bow-knot"
                style={{
                  backgroundColor: envelopeStyle.bowColor,
                  borderColor: envelopeStyle.ribbonColor,
                }}
              >
                <div
                  className="bow-left"
                  style={{ backgroundColor: envelopeStyle.bowColor }}
                />
                <div
                  className="bow-right"
                  style={{ backgroundColor: envelopeStyle.bowColor }}
                />
              </div>
            </motion.div>

            {/* Flaps */}
            <motion.div
              className="top-flap"
              style={{ borderTopColor: envelopeStyle.topFlapColor }}
              animate={isOpen ? {
                rotateX: -180,
                transition: { duration: 0.6, delay: 0.5 }
              } : {}}
            />
            <div
              className="bottom-flap"
              style={{ borderBottomColor: envelopeStyle.bottomFlapColor }}
            />
          </div>
        </motion.div>

        {/* Card */}
        <motion.div
          className="card-wrapper"
          initial={{ y: 0, scale: 1, opacity: 0 }}
          animate={isOpen ? {
            y: -100,
            scale: 1.15,
            opacity: 1,
            transition: { duration: 1, delay: 0.7 }
          } : {}}
        >
          <div
            className="card"
            style={{
              backgroundColor: card.theme_color || '#7C4DFF',
            }}
          >
            {getCoverImageUrl() && (
              <img
                src={getCoverImageUrl()}
                alt={card.title}
                className="card-cover"
              />
            )}
          </div>
        </motion.div>

        {/* Tap to Open Text */}
        {!isOpen && (
          <motion.p
            className="tap-text"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Tap to open!
          </motion.p>
        )}
      </div>

      {/* Card Details (shown after opening) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="card-details"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.5 }}
          >
            <h2 style={getFontStyle()}>{card.title}</h2>
            {card.notes && <p style={getFontStyle()}>{card.notes}</p>}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Buttons (shown after opening) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="action-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.5 }}
          >
            <button className="btn btn-primary" onClick={handleShare}>
              <FiShare2 /> Share Card
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/create-greeting-card')}
            >
              <FiMail /> Create Your Own
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ViewGreetingCard;
